/**
 * Utility for making API calls with retry logic and exponential backoff
 * This helps reduce false "offline" notifications by retrying failed requests
 * before considering the network truly unavailable
 */

const DEFAULT_RETRY_CONFIG = {
    maxRetries: 3,
    baseDelay: 1000, // 1 second
    maxDelay: 8000,  // 8 seconds
    backoffMultiplier: 2
};

/**
 * Checks if an error is retryable (network-related)
 * @param {Error} error - The error to check
 * @returns {boolean} - Whether the error should trigger a retry
 */
const isRetryableError = (error) => {
    // Network errors that should trigger retries
    const retryableMessages = [
        'fetch',
        'network',
        'timeout',
        'connection',
        'offline',
        'unreachable'
    ];
    
    const errorMessage = error.message.toLowerCase();
    
    // Check for network-related error messages
    const isNetworkError = retryableMessages.some(msg => errorMessage.includes(msg));
    
    // Check for specific error types
    const isTypeError = error instanceof TypeError; // Often indicates network issues
    const isFetchError = error.name === 'TypeError' && errorMessage.includes('fetch');
    
    // Don't retry authentication errors (401) or client errors (4xx except 408, 429)
    if (error.status) {
        if (error.status === 401 || error.status === 403) return false;
        if (error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429) return false;
        // Don't retry 503 Service Unavailable - let it fail immediately to show proper error
        if (error.status === 503) return false;
    }
    
    return isNetworkError || isTypeError || isFetchError;
};

/**
 * Delays execution for the specified number of milliseconds
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise} - Promise that resolves after the delay
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculates the delay for the next retry using exponential backoff
 * @param {number} attempt - Current attempt number (0-based)
 * @param {object} config - Retry configuration
 * @returns {number} - Delay in milliseconds
 */
const calculateDelay = (attempt, config) => {
    const exponentialDelay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt);
    // Add some jitter to prevent thundering herd
    const jitter = Math.random() * 0.1 * exponentialDelay;
    return Math.min(exponentialDelay + jitter, config.maxDelay);
};

/**
 * Makes an API call with retry logic
 * @param {Function} apiCall - Function that returns a Promise for the API call
 * @param {object} retryConfig - Configuration for retry behavior
 * @returns {Promise} - Promise that resolves with the API response or rejects after all retries
 */
export const apiCallWithRetry = async (apiCall, retryConfig = {}) => {
    const config = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
    let lastError;
    
    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
        try {
            // Attempt the API call
            const result = await apiCall();
            
            // If we get here, the call succeeded
            if (attempt > 0) {
                console.log(`API call succeeded after ${attempt} retries`);
            }
            
            return result;
        } catch (error) {
            lastError = error;
            
            // If this is the last attempt, don't retry
            if (attempt === config.maxRetries) {
                console.error(`API call failed after ${config.maxRetries} retries:`, error);
                break;
            }
            
            // Check if this error should trigger a retry
            if (!isRetryableError(error)) {
                console.log('Non-retryable error, not retrying:', error.message);
                break;
            }
            
            // Calculate delay for next attempt
            const delayMs = calculateDelay(attempt, config);
            console.log(`API call failed (attempt ${attempt + 1}/${config.maxRetries + 1}), retrying in ${delayMs}ms:`, error.message);
            
            // Wait before retrying
            await delay(delayMs);
        }
    }
    
    // If we get here, all retries failed
    throw lastError;
};

/**
 * Wraps a fetch call with retry logic
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options
 * @param {object} retryConfig - Retry configuration
 * @returns {Promise<Response>} - Promise that resolves with the fetch response
 */
export const fetchWithRetry = async (url, options = {}, retryConfig = {}) => {
    return apiCallWithRetry(async () => {
        const response = await fetch(url, options);
        
        // For HTTP errors, create an error object with status
        if (!response.ok) {
            const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
            error.status = response.status;
            error.response = response;
            throw error;
        }
        
        return response;
    }, retryConfig);
};

export default apiCallWithRetry;
